import React, { useEffect, useState } from "react";
import api from "../utils/base_url";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { addCart, delCart } from "../redux/action";
import { toProductCartItem, getCartQtyFor } from "../redux/reducer/Carthelpers";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import '../styles/OfferModal.css';

const OfferModal = () => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selectedCat, setSelectedCat] = useState("all");
    const [filtered, setFiltered] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [favorites, setFavorites] = useState([]);

    const [stockError, setStockError] = useState({});
    const [disabledQty, setDisabledQty] = useState({});


    const [animatingHeart, setAnimatingHeart] = useState(null);

    const { user } = useAuth();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const state = useSelector((state) => state.handleCart);
    // API call for offer products by page/category
    const fetchOfferProducts = async (pageNo = 1, category = null) => {
        setLoading(true);
        try {
            let url = `productlist/?offer_only=true&page=${pageNo}`;
            const res = await api.get(url);
            setPage(res.data.current_page || 1);
            setTotalPages(res.data.total_pages || 1);
            const cats = Array.from(new Set(res.data.data.map(prod => prod.category_name)));
            setCategories(cats);
            setFiltered(
                category && category !== "all"
                    ? res.data.data.filter(prod => prod.category_name === category)
                    : res.data.data
            );
        } catch (err) {
            setFiltered([]);
        } finally {
            setLoading(false);
        }
    };
    const fetchFavorites = React.useCallback(async () => {
        if (!user) return;
        try {
            const res = await api.get(`favorites/ids/?auth_id=${user.id}`);
            setFavorites(res.data.favorites || []);
        } catch { }
    }, [user]);

    useEffect(() => {
        if (show) fetchFavorites();
    }, [show, fetchFavorites]);



    const toggleFavorite = async (productId) => {
        if (!user) {
            toast.error("Please login first!");
            navigate("/login");
            return;
        }

        try {
            const res = await api.post("favorites/toggle/", {
                product_id: productId,
                auth_id: user.id
            });
            const isFav = res.data.is_favorite ?? res.data.favorite ?? false;


            setFavorites((prev) =>
                isFav ? [...prev, productId] : prev.filter((id) => id !== productId)
            );
            setAnimatingHeart(productId);

            // Reset animation after 400ms
            setTimeout(() => setAnimatingHeart(null), 400);
            fetchFavorites();
        } catch {
            toast.error("Failed. Try again.");
        }
    };
    useEffect(() => {
        if (show) fetchOfferProducts(page, selectedCat);
    }, [show, page, selectedCat]);

    const handleCategory = cat => {
        setSelectedCat(cat);
        setPage(1); // Reset to page 1 when category changes
        fetchOfferProducts(1, cat);
    };

    const handlePageChange = pageNum => {
        setPage(pageNum);
    };

    const getCartQty = (productId) => getCartQtyFor(state, "product", productId);

    const addItem = (product) => {
        const availableStock = product.stock_quantity;

        if (availableStock && availableStock > 0 && getCartQty(product.id) >= availableStock) {
            // Show error badge
            setStockError(prev => ({ ...prev, [product.id]: true }));
            setDisabledQty(prev => ({ ...prev, [product.id]: true }));

            setTimeout(() => {
                setStockError(prev => ({ ...prev, [product.id]: false }));
                setDisabledQty(prev => ({ ...prev, [product.id]: false }));
            }, 2000);

            return;
        }

        dispatch(addCart(toProductCartItem(product)));
    };

    const removeItem = (product) => {
        dispatch(delCart(toProductCartItem(product)));
        setStockError(prev => {
            const newState = { ...prev };
            delete newState[product.id];
            return newState;
        });
    };

    return (
        <>
            <button
                onClick={() => setShow(true)}
                className="offer-trigger-btn"
            >
                <div className="offer-trigger-label">
                    <span className="offer-trigger-text">
                        Offers
                    </span>

                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="1.3em"
                        height="1.3em"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                    >
                        <path d="M2 3a1 1 0 0 1 1-1h8a1 1 0 0 1 .707.293l10 10a1 1 0 0 1 0 1.414l-8 8a1 1 0 0 1-1.414 0l-10-10A1 1 0 0 1 2 11V3zm2 1v6.586l9 9L19.586 13l-9-9H4z" />
                        <path d="M9 7.5a1.5 1.5 0 1 1-3 0a1.5 1.5 0 0 1 3 0z" />
                    </svg>
                </div>
            </button>




            {/* --- Modal --- */}
            {show && (
                <>
                    <div className="offer-modal-backdrop" onClick={() => setShow(false)} ></div>
                    <div className="offer-modal-content offer-modal-scope" onClick={e => e.stopPropagation()}>
                        <div className="offer-modal-header">
                            <h3 className="offer-modal-title">Offer Products</h3>
                            <button className="offer-close-btn" onClick={() => setShow(false)}>
                                ×
                            </button>
                        </div>
                        {/* Category Group */}
                        <div className="offer-cat-strip">
                            <button className={`cat-btn ${selectedCat === "all" ? "active" : ""}`} onClick={() => handleCategory("all")}>
                                All
                            </button>
                            {categories.map(cat => (
                                <button key={cat}
                                    className={`cat-btn ${selectedCat === cat ? "active" : ""}`}
                                    onClick={() => handleCategory(cat)}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                        {/* Grid */}
                        <div className="offer-modal-body">
                            <div className="offer-grid">
                                {loading
                                    ? [...Array(4)].map((_, idx) => (
                                        <div key={idx} className="skeleton-card">
                                            <Skeleton height={280} />
                                        </div>
                                    ))
                                    : filtered.length === 0 ? (
                                        <div className="offer-empty">
                                            <div className="offer-empty-icon">
                                                🌿
                                            </div>

                                            <div className="offer-empty-title">
                                                No offer products found
                                            </div>
                                        </div>

                                    ) : (
                                        filtered.map((product, idx) => {
                                            const outOfStock = !product.is_available || product.stock_quantity === 0;
                                            const lowStock = product.stock_quantity <= 2 && product.stock_quantity > 0;
                                            return (
                                                <div
                                                    key={product.id}
                                                    className="product-card "

                                                    onClick={() => {
                                                        setShow(false);        // close modal
                                                        navigate(`/product/${product.id}`);
                                                    }}

                                                >
                                                    {/* ❤️ Favorite Icon */}
                                                    <motion.div
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleFavorite(product.id);
                                                        }}
                                                        className="offer-fav-btn"
                                                    >
                                                        {user ? (
                                                            <div className="fav-circle">
                                                                {favorites.some(
                                                                    (fav) => (typeof fav === "number" ? fav === product.id : fav.id === product.id)
                                                                ) ? (
                                                                    <motion.span
                                                                        className="favorite-icon active"
                                                                        animate={
                                                                            animatingHeart === product.id
                                                                                ? {
                                                                                    scale: [1, 1.35, 1],
                                                                                }
                                                                                : {}
                                                                        }
                                                                        transition={{
                                                                            duration: 0.3,
                                                                        }}
                                                                    >
                                                                        ❤️
                                                                    </motion.span>
                                                                ) : (
                                                                    <motion.span
                                                                        className="favorite-icon inactive"
                                                                        whileTap={{ scale: 0.9 }}
                                                                    >
                                                                        🤍
                                                                    </motion.span>
                                                                )}
                                                            </div>
                                                        ) : null}

                                                    </motion.div>


                                                    {/* 🔥 Offer Badge */}
                                                    {product.offer_percentage && (
                                                        <span className="offer-badge">
                                                            {Math.round(product.offer_percentage)}% OFF
                                                        </span>
                                                    )}
                                                    {outOfStock && (
                                                        <span
                                                            style={{
                                                                position: "absolute",
                                                                top: 45,
                                                                left: 0,
                                                                background: "#dc3545",
                                                                color: "#fff",
                                                                padding: "4px 12px",
                                                                fontSize: "0.75rem",
                                                                fontWeight: 600,
                                                                borderRadius: "0 6px 6px 0",
                                                                zIndex: 12
                                                            }}
                                                        >
                                                            Out of Stock
                                                        </span>
                                                    )}

                                                    {!outOfStock && lowStock && (
                                                        <span className="stock-badge out-stock">
                                                            Hurry! Only {product.stock_quantity} left
                                                        </span>
                                                    )}
                                                    {stockError[product.id] && (
                                                        <motion.span
                                                            initial={{ scale: 0.8, opacity: 0 }}
                                                            animate={{ scale: [0.8, 1.05, 1], opacity: 1 }}
                                                            exit={{ scale: 0.8, opacity: 0 }}
                                                            transition={{ duration: 0.3, ease: "easeOut" }}
                                                            className="stock-error-badge"
                                                        >
                                                            Stock limit reached!
                                                        </motion.span>
                                                    )}
                                                    <div className="img-wrapper" >
                                                        <img
                                                            src={product.product_image}
                                                            alt={product.product_name}
                                                            className="offer-img"
                                                        />
                                                    </div>

                                                    <div className="product-content">
                                                        <h5 className="product-title">
                                                            {product.product_name.length > 15
                                                                ? product.product_name.slice(0, 15) + "..."
                                                                : product.product_name}
                                                        </h5>

                                                        <div className="product-price-wrap">
                                                            {product.offer_price ? (
                                                                <>
                                                                    <span className="product-price-offer">
                                                                        ₹ {parseFloat(product.offer_price).toLocaleString()}
                                                                    </span>
                                                                    <span className="product-price-original">
                                                                        <s>₹ {parseFloat(product.price).toLocaleString()}</s>
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                <span className="product-price">
                                                                    ₹ {parseFloat(product.price).toLocaleString()}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="d-flex justify-content-center pb-3">
                                                        <div className="product-btns" onClick={(e) => e.stopPropagation()}>
                                                            {product.stock_quantity === 0 ? (
                                                                <span className="out-stock-label">Out of Stock</span>
                                                            ) : getCartQty(product.id) === 0 ? (
                                                                <button
                                                                    className="btn-buy"
                                                                    onClick={() => addItem(product)}
                                                                >
                                                                    Add to Cart
                                                                </button>
                                                            ) : (
                                                                <div className="qty-box">
                                                                    <button
                                                                        className="qty-btn"
                                                                        onClick={() => removeItem(product)}
                                                                    >
                                                                        <i className="fas fa-minus"></i>
                                                                    </button>

                                                                    <span className="qty-value">{getCartQty(product.id)}</span>

                                                                    <button
                                                                        className="qty-btn"
                                                                        onClick={() => addItem(product)}
                                                                        disabled={disabledQty[product.id]}
                                                                    >
                                                                        <i className="fas fa-plus"></i>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                            );
                                        })
                                    )}
                            </div>
                            {/* Pagination */}
                            <div className="offer-pagination">
                                <div >

                                    <button
                                        className="pagenation-button"
                                        disabled={page === 1}
                                        onClick={() => handlePageChange(page - 1)}
                                    >
                                        ‹ Prev
                                    </button>
                                </div>
                                <button
                                    className="pagenation-button"
                                    disabled={page === totalPages}
                                    onClick={() => handlePageChange(page + 1)}
                                >
                                    Next ›
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default OfferModal;
